var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { FilterOutlined, LeftOutlined, RightOutlined, SortAscendingOutlined } from '@ant-design/icons';
import services from 'services';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { monoColumn, Muted, NameText } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, ListExportButton, ListSecondaryButton, listPager, usePagedRows } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { ProductCategory as ProductCategoryResource } from 'constants/resources';
import { downloadCsv, todayStamp } from '../csv';
import ProductCategoryDrawer from './components/ProductCategoryDrawer';
import './style.css';
var ProductCategories = function (props) {
    var t = props.translate;
    var productCategoryService = services.productCategoryService, productService = services.productService, permissionService = services.permissionService;
    var canAdd = permissionService.hasPermissionToAdd(ProductCategoryResource);
    var _a = productCategoryService.useProductCategories(), data = _a.data, isLoading = _a.isLoading;
    var productPage = productService.useProducts(1, { items: 1000 }).data;
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var _c = useState(true), sortAsc = _c[0], setSortAsc = _c[1];
    var _d = useState(false), exporting = _d[0], setExporting = _d[1];
    var _e = useState(false), drawerOpen = _e[0], setDrawerOpen = _e[1];
    var debounceRef = useRef(undefined);
    var categories = data || [];
    var productCounts = useMemo(function () {
        var _a;
        var products = (productPage === null || productPage === void 0 ? void 0 : productPage.products) || [];
        var total = Number(((_a = productPage === null || productPage === void 0 ? void 0 : productPage.meta) === null || _a === void 0 ? void 0 : _a.count) || 0);
        if (!productPage || total > products.length)
            return null;
        var counts = new Map();
        var mapped = 0;
        products.forEach(function (product) {
            var _a;
            var categoryId = ((_a = product.getProductCategory()) === null || _a === void 0 ? void 0 : _a.getId()) || product.getProductCategoryId();
            if (!categoryId)
                return;
            mapped += 1;
            counts.set(categoryId, (counts.get(categoryId) || 0) + 1);
        });
        if (products.length > 0 && mapped === 0)
            return null;
        return counts;
    }, [productPage]);
    var filtered = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        var rows = !query
            ? __spreadArray([], categories, true) : categories.filter(function (category) {
            return [category.getId(), category.getName(), category.getCreatedAt()]
                .join(' ')
                .toLowerCase()
                .includes(query);
        });
        rows.sort(function (left, right) { return (sortAsc ? left.getId() - right.getId() : right.getId() - left.getId()); });
        return rows;
    }, [categories, searchValue, sortAsc]);
    var _f = usePagedRows(filtered), page = _f.page, setPage = _f.setPage, pages = _f.pages, from = _f.from, to = _f.to, pageRows = _f.pageRows;
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setPage(1);
            setSearchValue(value);
        }, 300);
    };
    var exportCategories = function () {
        if (exporting)
            return;
        if (!filtered.length) {
            message.warning(t('exportEmpty'));
            return;
        }
        setExporting(true);
        try {
            var rows = __spreadArray([
                [t('id'), t('name'), t('createdAt')]
            ], filtered.map(function (category) { return [
                String(category.getId()),
                category.getName() || '',
                category.getCreatedAt()
            ]; }), true);
            downloadCsv("product-categories-".concat(todayStamp(), ".csv"), rows);
        }
        catch (_a) {
            message.error(t('exportFailed'));
        }
        finally {
            setExporting(false);
        }
    };
    var columns = useMemo(function () { return __spreadArray(__spreadArray([
        monoColumn({ key: 'id', title: t('id'), width: '72px', variant: 'id', value: function (category) { return String(category.getId()).padStart(2, '0'); } }),
        {
            key: 'name',
            title: t('name'),
            width: 'minmax(180px, 1.6fr)',
            render: function (category) { return (_jsxs("div", { className: "pcat-name", children: [_jsx("span", { className: "pcat-dot", "aria-hidden": true }), _jsx(NameText, { children: category.getName() })] })); }
        }
    ], (productCounts ? [{
            key: 'count',
            title: t('productsCount'),
            width: '140px',
            render: function (category) { return (_jsx("span", { className: "cidx-pill", children: t('productsCountValue', { count: productCounts.get(category.getId()) || 0 }) })); }
        }] : []), true), [
        { key: 'created', title: t('createdAt'), width: 'minmax(140px, 1fr)', render: function (category) { return _jsx(Muted, { children: category.getCreatedAt() }); } }
    ], false); }, [t, productCounts]);
    return (_jsxs(ListScreen, { className: "pcat-list", title: t('title'), count: t('countBadge', { count: categories.length }), subtitle: t('subtitle'), actions: (_jsxs(_Fragment, { children: [_jsx(ListExportButton, { label: t('export'), busyLabel: t('exporting'), busy: exporting, onClick: exportCategories }), canAdd && _jsx(ListAddButton, { label: t('common:buttons.newProductCategory'), onClick: function () { return setDrawerOpen(true); } })] })), search: {
            placeholder: t('searchPlaceholder'),
            onChange: onSearchValueChange,
            icon: _jsx(FilterOutlined, { className: "cidx-search__icon" })
        }, toolbarEnd: (_jsx(ListSecondaryButton, { icon: _jsx(SortAscendingOutlined, {}), label: sortAsc ? t('sortAscending') : t('sortDescending'), onClick: function () {
                setPage(1);
                setSortAsc(function (current) { return !current; });
            } })), children: [_jsx(DataTable, { relaxed: true, minWidth: "640px", loading: isLoading, rows: pageRows, rowKey: function (category) { return category.getId(); }, empty: _jsx(EmptyComponent, { description: t('empty') }), columns: columns, pager: listPager({
                    total: filtered.length,
                    page: page,
                    pages: pages,
                    meta: (_jsxs(_Fragment, { children: [t('showingLead'), ' ', _jsxs("span", { className: "pcat-pager__strong", children: [from, "\u2013", to] }), ' ', t('showingMid'), ' ', _jsx("span", { className: "pcat-pager__strong", children: filtered.length }), ' ', t('showingTail')] })),
                    previous: _jsxs(_Fragment, { children: [_jsx(LeftOutlined, {}), _jsx("span", { children: t('previous') })] }),
                    next: _jsxs(_Fragment, { children: [_jsx("span", { children: t('next') }), _jsx(RightOutlined, {})] }),
                    center: _jsx("span", { className: "pcat-pager__current", children: page }),
                    setPage: setPage
                }) }), canAdd && (_jsx(ProductCategoryDrawer, { visiable: drawerOpen, onCloseDrawer: function () { return setDrawerOpen(false); } }))] }));
};
export default localeTranslator(ProductCategories, 'productCategories');
